///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { ChangeDetectionStrategy, Component, computed, ElementRef, HostListener, input, model, signal, viewChildren } from '@angular/core';
import { ButtonsDirectivesModule } from '../shared/directives/buttons-directives/buttons-directives.module';

@Component({
    selector: 'winde-email-verification-input',
    templateUrl: './email-verification-input.component.html',
    styleUrl: './email-verification-input.component.scss',
    imports: [ButtonsDirectivesModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class EmailVerificationInputComponent {
    protected readonly charBoxElements = viewChildren<ElementRef>('charBox');

    token = model<string[]>([]);
    tokenLength = input<number>(8);

    protected readonly currentIndex = signal(0);
    protected readonly charBoxes = computed(() => {
        const token = this.token();
        return Array.from({ length: this.tokenLength() }, (_, index) => ({
            value: token[index] || '',
            filled: !!token[index]
        }));
    });

    @HostListener('document:keydown', ['$event'])
    handleKeydown(event: KeyboardEvent) {
        if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'v') {
            return;
        }

        const keyCode = event.key.charCodeAt(0);
        const isPrintableAscii = event.key.length === 1 && keyCode >= 33 && keyCode <= 126;

        if (isPrintableAscii) {
            event.preventDefault();
            this.inputChar(event.key);
        } else if (event.key === 'Backspace') {
            event.preventDefault();
            this.deleteChar();
        } else if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.moveLeft();
        } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.moveRight();
        }
    }

    @HostListener('document:paste', ['$event'])
    handlePaste(event: ClipboardEvent) {
        const pastedText = event.clipboardData?.getData('text')?.trim() || '';
        const isPrintableAscii = /^[\x21-\x7E]+$/.test(pastedText);
        const isValidLength = pastedText.length === this.tokenLength();

        if (isValidLength && isPrintableAscii) {
            event.preventDefault();
            this.fillCode(pastedText);
        }
    }

    setActiveBox(index: number) {
        if (index >= 0 && index < this.tokenLength()) {
            this.currentIndex.set(index);

            // Focus the element after view update
            setTimeout(() => {
                const boxElement = this.charBoxElements()[index];
                if (boxElement) {
                    boxElement.nativeElement.focus();
                }
            }, 0);
        }
    }

    private inputChar(char: string) {
        const currentIdx = this.currentIndex();
        if (currentIdx < this.tokenLength()) {
            this.token.update((code) => {
                const newCode = [...code];
                newCode[currentIdx] = char;
                return newCode;
            });

            if (currentIdx < this.tokenLength() - 1) {
                this.moveRight();
            }
        }
    }

    private deleteChar() {
        const currentIdx = this.currentIndex();
        let targetIndex = currentIdx;

        if (!this.token()[currentIdx] && currentIdx > 0) {
            targetIndex = currentIdx - 1;
        }

        this.token.update((code) => {
            const newCode = [...code];
            newCode[targetIndex] = '';
            return newCode;
        });
        this.setActiveBox(targetIndex);
    }

    private fillCode(code: string) {
        const codeArray = code.split('').concat(Array(this.tokenLength()).fill('')).slice(0, this.tokenLength());
        this.token.set(codeArray);
        if (code.length === this.tokenLength()) {
            this.currentIndex.set(-1);
        } else {
            this.setActiveBox(code.length);
        }
    }

    private moveLeft() {
        const currentIdx = this.currentIndex();
        if (currentIdx > 0) {
            this.setActiveBox(currentIdx - 1);
        }
    }

    private moveRight() {
        const currentIdx = this.currentIndex();
        if (currentIdx < this.tokenLength() - 1) {
            this.setActiveBox(currentIdx + 1);
        }
    }
}
